import { useState, useEffect } from 'react';

interface Gas {
  name: string;
  lifetime: number; // in years
  color: string;
  gwp: number;
}

export default function GasLifetimeVisualization() {
  const [currentYear, setCurrentYear] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const gases: Gas[] = [
    { name: 'Methane (CH₄)', lifetime: 12, color: '#f97316', gwp: 28 },
    { name: 'Carbon Dioxide (CO₂)', lifetime: 100, color: '#2E8B57', gwp: 1 },
    { name: 'Nitrous Oxide (N₂O)', lifetime: 114, color: '#3b82f6', gwp: 265 },
  ];

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentYear((prev) => {
          if (prev >= 120) {
            setIsPlaying(false);
            return 120;
          }
          return prev + 1;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  const handleReset = () => {
    setCurrentYear(0);
    setIsPlaying(false);
  };

  const handlePlay = () => {
    if (currentYear >= 120) {
      setCurrentYear(0);
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="bg-white rounded-2xl p-8 border border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        Atmospheric Lifetime Comparison
      </h3>

      <p className="text-gray-600 text-sm mb-6">
        Watch how different greenhouse gases persist in the atmosphere over time. Even though methane is more potent, it breaks down much faster than CO₂.
      </p>

      {/* Timeline */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-medium text-gray-700">
            Year {currentYear} after release
          </span>
          <div className="flex gap-2">
            <button
              onClick={handlePlay}
              className="px-4 py-2 bg-[#2E8B57] text-white rounded-lg text-sm font-medium hover:bg-[#257047] transition-colors"
            >
              {isPlaying ? 'Pause' : currentYear >= 120 ? 'Replay' : 'Play'}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="relative h-2 bg-gray-200 rounded-full mb-2">
          <div
            className="absolute h-full bg-[#2E8B57] rounded-full transition-all duration-100"
            style={{ width: `${(currentYear / 120) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0 years</span>
          <span>120 years</span>
        </div>
      </div>

      {/* Gas Bars */}
      <div className="space-y-6">
        {gases.map((gas) => {
          const remainingPercentage = currentYear <= gas.lifetime 
            ? 100 - (currentYear / gas.lifetime) * 100 
            : 0;

          return (
            <div key={gas.name}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-semibold text-gray-900">{gas.name}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    (Lifetime: {gas.lifetime} years, GWP: {gas.gwp})
                  </span>
                </div>
                <span className="text-sm font-medium" style={{ color: gas.color }}>
                  {remainingPercentage.toFixed(0)}% remains
                </span>
              </div>
              <div className="bg-gray-100 rounded-lg overflow-hidden h-10">
                <div
                  className="h-full rounded-lg transition-all duration-100 flex items-center justify-center"
                  style={{
                    width: `${remainingPercentage}%`,
                    backgroundColor: gas.color,
                    opacity: 0.3 + (remainingPercentage / 100) * 0.7,
                  }}
                >
                  {remainingPercentage > 20 && (
                    <span className="text-white text-xs font-semibold">
                      {remainingPercentage.toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Insights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <h4 className="font-semibold text-gray-900 text-sm mb-2">🔥 Short-term Impact</h4>
          <p className="text-xs text-gray-700">
            Methane's high GWP means reducing methane emissions can quickly slow warming in the next few decades.
          </p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-gray-900 text-sm mb-2">📊 Long-term Challenge</h4>
          <p className="text-xs text-gray-700">
            CO₂'s century-long lifetime makes it crucial to reduce emissions now to prevent irreversible warming.
          </p>
        </div>
      </div>
    </div>
  );
}
